import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslations } from '../../hooks/useTranslations';

function Settings() {
  const [settings, setSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const { getAuthHeader } = useAdminAuth();
  const { currentLanguage, changeLanguage } = useLanguage();
  const { t } = useTranslations();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await fetch('https://api.maxprofitacademy.com/admin/settings', {
        headers: getAuthHeader()
      });

      if (!response.ok) {
        // Не показываем ошибку, просто не загружаем настройки
        console.warn('Settings API not available, continuing without backend settings');
        setIsLoading(false);
        return;
      }

      const data = await response.json();
      setSettings(data);
    } catch (err) {
      // Не показываем ошибку пользователю, просто логируем
      console.warn('Error fetching settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      const response = await fetch('https://api.maxprofitacademy.com/admin/settings', {
        method: 'PUT',
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (!response.ok) {
        throw new Error('Failed to update settings');
      }

      setSuccessMessage('Настройки успешно обновлены');
    } catch (err) {
      setError('Ошибка при обновлении настроек');
      console.error('Error updating settings:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">{t('adminSettings.title')}</h2>
        <p className="text-gray-600 mt-1">{t('adminSettings.subtitle')}</p>
      </div>

      {error && error.includes('обновлении') && (
        <div className="mb-6 p-4 bg-red-50 text-red-700 rounded-xl">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 text-green-700 rounded-xl">
          {successMessage}
        </div>
      )}

      {/* Language Settings */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t('adminSettings.language.title')}</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">{t('adminSettings.language.label')}</p>
            <p className="text-xs text-gray-500">{t('adminSettings.language.description')}</p>
          </div>
          <div className="flex items-center space-x-3">
            <span className={`text-sm font-medium ${currentLanguage === 'ru' ? 'text-primary' : 'text-gray-400'}`}>RU</span>
            <button
              type="button"
              onClick={() => changeLanguage(currentLanguage === 'ru' ? 'en' : 'ru')}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                currentLanguage === 'en' ? 'bg-primary' : 'bg-gray-300'
              }`}
              role="switch"
              aria-checked={currentLanguage === 'en'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  currentLanguage === 'en' ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${currentLanguage === 'en' ? 'text-primary' : 'text-gray-400'}`}>EN</span>
          </div>
        </div>
      </div>

      {settings && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('adminSettings.general.title')}</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="siteName" className="block text-sm font-medium text-gray-700 mb-1">
                  Название сайта
                </label>
                <input
                  type="text"
                  id="siteName"
                  value={settings.siteName}
                  onChange={(e) => setSettings({ ...settings, siteName: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
              <div>
                <label htmlFor="siteDescription" className="block text-sm font-medium text-gray-700 mb-1">
                  Описание сайта
                </label>
                <textarea
                  id="siteDescription"
                  value={settings.siteDescription}
                  onChange={(e) => setSettings({ ...settings, siteDescription: e.target.value })}
                  className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  rows="3"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Настройки регистрации</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Требования к паролю
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="requireUppercase"
                      checked={settings.passwordRequirements.requireUppercase}
                      onChange={(e) => setSettings({
                        ...settings,
                        passwordRequirements: {
                          ...settings.passwordRequirements,
                          requireUppercase: e.target.checked
                        }
                      })}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="requireUppercase" className="ml-2 text-sm text-gray-700">
                      Требовать заглавные буквы
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="requireNumbers"
                      checked={settings.passwordRequirements.requireNumbers}
                      onChange={(e) => setSettings({
                        ...settings,
                        passwordRequirements: {
                          ...settings.passwordRequirements,
                          requireNumbers: e.target.checked
                        }
                      })}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="requireNumbers" className="ml-2 text-sm text-gray-700">
                      Требовать цифры
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="requireSpecialChars"
                      checked={settings.passwordRequirements.requireSpecialChars}
                      onChange={(e) => setSettings({
                        ...settings,
                        passwordRequirements: {
                          ...settings.passwordRequirements,
                          requireSpecialChars: e.target.checked
                        }
                      })}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="requireSpecialChars" className="ml-2 text-sm text-gray-700">
                      Требовать специальные символы
                    </label>
                  </div>
                </div>
              </div>
              <div>
                <label htmlFor="minPasswordLength" className="block text-sm font-medium text-gray-700 mb-1">
                  Минимальная длина пароля
                </label>
                <input
                  type="number"
                  id="minPasswordLength"
                  value={settings.passwordRequirements.minLength}
                  onChange={(e) => setSettings({
                    ...settings,
                    passwordRequirements: {
                      ...settings.passwordRequirements,
                      minLength: parseInt(e.target.value)
                    }
                  })}
                  className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Настройки уведомлений</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email уведомления
                </label>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="notifyNewUsers"
                      checked={settings.notifications.notifyNewUsers}
                      onChange={(e) => setSettings({
                        ...settings,
                        notifications: {
                          ...settings.notifications,
                          notifyNewUsers: e.target.checked
                        }
                      })}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="notifyNewUsers" className="ml-2 text-sm text-gray-700">
                      Уведомлять о новых пользователях
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="notifyPayments"
                      checked={settings.notifications.notifyPayments}
                      onChange={(e) => setSettings({
                        ...settings,
                        notifications: {
                          ...settings.notifications,
                          notifyPayments: e.target.checked
                        }
                      })}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <label htmlFor="notifyPayments" className="ml-2 text-sm text-gray-700">
                      Уведомлять о платежах
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-3 bg-primary text-black font-medium rounded-xl hover:bg-primary/90 transition-colors"
            >
              Сохранить изменения
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

export default Settings; 